import { useEffect, useMemo, useRef, useState } from "react";
import { Pause, Play, ChevronLeft, ChevronRight, Maximize2 } from "lucide-react";
import { CutInSlide } from "@/components/CutInSlide";
import { useImpact } from "@/components/WeatherImpact";
import { api } from "@/lib/api";
import { TvRadarMap, RadarLegend, WindLegend, SpcLegend, TvAviationMap, AviationLegend } from "@/components/broadcast/TvMap";
import SlideStinger, { stingerForSlide } from "@/components/broadcast/SlideStinger";
import { groupAlerts, AlertsLegend } from "@/components/broadcast/AlertMaps";
import { useHistoryFacts, HistorySlide } from "@/components/broadcast/HistorySlide";
import { useLakes, LakesSlide } from "@/components/broadcast/LakesSlide";
import { useCoast, CoastSlide } from "@/components/broadcast/CoastSlide";
import { usePollen, PollenSlide } from "@/components/broadcast/PollenSlide";
import { useDrought, DroughtSlide } from "@/components/broadcast/DroughtSlide";
import { useOutages, OutagesSlide } from "@/components/broadcast/OutagesSlide";
import { useStormReports, StormReportsSlide, StormReportsLegend } from "@/components/broadcast/StormReportsSlide";
import { useThreatIndex, ThreatIndexSlide } from "@/components/broadcast/ThreatIndexSlide";
import { SafetySlide } from "@/components/broadcast/SafetySlide";
import { useRadarArrivals, StormArrivalSlide } from "@/components/broadcast/StormArrivalSlide";
import { useThreatSummary, ThreatSummarySlide } from "@/components/broadcast/ThreatSummarySlide";
import { SevereOutlookSlide, SEVERE_RISKS } from "@/components/broadcast/SevereOutlookSlide";
import { MinutecastSlide, hasMinutecast } from "@/components/broadcast/MinutecastSlide";
import { useOutlookScore, OutlookScoreSlide, hasOutlookScore } from "@/components/broadcast/OutlookScoreSlide";
import { useSpotlight, TownSpotlightSlide } from "@/components/broadcast/TownSpotlightSlide";
import { useChatTip, ChatTipBug } from "@/components/broadcast/ChatTipBug";
import { useHooch, HoochSlide } from "@/components/broadcast/HoochSlide";
import { FridayNightSlide, buildKickoffGames } from "@/components/broadcast/FridayNightSlide";
import { useAg, AgSlide } from "@/components/broadcast/AgSlide";
import { useEvents, EventsSlide } from "@/components/broadcast/EventsSlide";
import { GamedaySlide } from "@/components/broadcast/GamedaySlide";
import { TvFrame, TvTicker } from "@/components/broadcast/TvFrame";
import { buildTickerFeed } from "@/components/broadcast/tickerFeed";
import { SLIDE_MS, tonightPeriod, tomorrowPeriod, _isCamDead, warmForecastVideos } from "@/components/broadcast/shared";
import { IntroSlide } from "@/components/broadcast/slides/IntroSlide";
import { FeatureCitySlide, ExtremesSlide, BentoSlide, SevenDaySlide, TempOutlookSlide, GATempsSlide, GAFeelsLikeSlide, GADewpointsSlide, WindGustsSlide } from "@/components/broadcast/slides/TemperatureSlides";
import { AlertSlide, GAAlertsMapSlide, GASevereOutlookSlide, VelocitySlide } from "@/components/broadcast/slides/AlertSlides";
import { TropicalUpdateSlide, TropicalConeSlide, TropicalOutlookSlide } from "@/components/broadcast/slides/TropicalSlides";
import { CameraMapSlide, LiveCameraWallSlide, FullScreenCamSlide } from "@/components/broadcast/slides/CameraSlides";
import { ForecastVideoSlide, BumperSlide, SponsorSlide, ArticleSlide, ImpactSlide } from "@/components/broadcast/slides/MediaSlides";
import { PromoSlide } from "@/components/broadcast/slides/PromoSlide";
import { AviationBoardSlide, AviationHazardsSlide } from "@/components/broadcast/slides/AviationSlides";
import { AlertTakeover, isTakeoverWarning } from "@/components/broadcast/slides/AlertTakeover";
import { OnTheEightsSlide } from "@/components/broadcast/slides/OnTheEightsSlide";
export { SITE_DOMAIN } from "@/components/broadcast/shared";

const CONDITIONS_SLIDE_ORDER = ["map", "ga-temps-map", "feature", "bento", "extremes", "seven"];

// BroadcastTicker — a global admin-composed scrolling ticker rendered at the
// very bottom of the broadcast loop, above any per-slide LiveDataTicker. Items
// with priority=urgent get red styling; routine items are dark chrome.
function BroadcastTicker({ items }) {
  if (!items || items.length === 0) return null;
  const hasUrgent = items.some((i) => i.priority === "urgent");
  const spans = items.flatMap((it, i) => ([
    <span key={`t-${it.id}-${i}`} className="mx-4 whitespace-nowrap font-oswald text-[11px] uppercase tracking-widest text-white">
      {it.priority === "urgent" && (
        <span className="mr-1.5 rounded-sm bg-white px-1 py-px font-oswald text-[8px] font-bold uppercase tracking-widest text-[#d90429]">
          Urgent
        </span>
      )}
      {it.text}
      <span className="mx-4 text-[#ffd60a]">◆</span>
    </span>
  ]));
  return (
    <div
      data-testid="broadcast-ticker"
      className={`pointer-events-none absolute inset-x-0 bottom-0 z-40 border-t backdrop-blur-sm ${
        hasUrgent ? "border-white bg-[#d90429]" : "border-[#ffd60a] bg-black/90"
      }`}
    >
      <div className="flex items-center">
        <div className={`whitespace-nowrap border-r border-white/10 px-2 py-1 font-oswald text-[10px] font-bold uppercase tracking-widest text-white ${
          hasUrgent ? "bg-[#a10322]" : "bg-[#ffd60a] text-black"
        }`}>
          GAWX · News
        </div>
        <div className="overflow-hidden py-px">
          <div className="marquee-track">
            {spans}
            {spans}
          </div>
        </div>
      </div>
    </div>
  );
}

// NextUpBug — a small persistent corner tag naming the board coming up next so
// viewers know a segment they care about is on the way and stick around for it.
// Hidden during breaking segments (handled by the caller). Encoder-lite safe:
// solid chrome, no backdrop blur or animated paints.
function NextUpBug({ title }) {
  if (!title) return null;
  return (
    <div
      data-testid="broadcast-next-up"
      className="next-up-bug pointer-events-none absolute right-[20px] top-[22px] z-30 flex max-w-[400px] items-stretch overflow-hidden rounded-[3px] bg-[#0a0f1c] shadow-[0_6px_20px_rgba(0,0,0,0.5)]"
    >
      <span className="flex shrink-0 items-center bg-[#ffd60a] px-3 font-oswald text-[15px] font-black uppercase tracking-[0.16em] text-[#0a0f1c]">
        Up Next
      </span>
      <span
        data-testid="broadcast-next-up-title"
        className="min-w-0 truncate px-4 py-2.5 font-oswald text-[24px] font-black uppercase tracking-wide text-white"
      >
        {title}
      </span>
    </div>
  );
}

const avgRight = (label, vals) => {
  const nums = vals.filter((v) => typeof v === "number");
  if (!nums.length) return null;
  return `${label}: ${Math.round(nums.reduce((a, b) => a + b, 0) / nums.length)}°`;
};

export function useBroadcastState({ durationMs: durationOverride, manual = false, capture = false } = {}) {
  const impact = useImpact();
  const historyFacts = useHistoryFacts();
  const lakes = useLakes();
  const coast = useCoast();
  const pollen = usePollen();
  const drought = useDrought();
  const outages = useOutages();
  const stormReports = useStormReports();
  const threat = useThreatIndex();
  const radarArrivals = useRadarArrivals();
  const threatSummary = useThreatSummary();
  const outlook = useOutlookScore();
  const spotlight = useSpotlight();
  const hooch = useHooch();
  const ag = useAg();
  const events = useEvents();
  const [cities, setCities] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [cameras, setCameras] = useState([]);
  const [config, setConfig] = useState(null);
  const [stormMode, setStormMode] = useState(false);
  const [velocity, setVelocity] = useState(null);
  const [bumper, setBumper] = useState(null);
  const [sponsors, setSponsors] = useState([]);
  const [article, setArticle] = useState(null);
  const [bravesGame, setBravesGame] = useState(null);
  const [ugaGame, setUgaGame] = useState(null);
  const [falconsGame, setFalconsGame] = useState(null);
  const [techGame, setTechGame] = useState(null);
  const [tickerItems, setTickerItems] = useState([]);
  const [spcDays, setSpcDays] = useState([]);
  const [spcAll, setSpcAll] = useState([]);
  const [tropical, setTropical] = useState(null);
  const [aviation, setAviation] = useState(null);
  const [forecastVideos, setForecastVideos] = useState(null);
  const [promos, setPromos] = useState(null);
  const [fx, setFx] = useState(null);
  const [takeover, setTakeover] = useState(null);
  const [eights, setEights] = useState(null);
  const seenWarnRef = useRef(null);
  const eightsSlotRef = useRef(null);
  const [cutIn, setCutIn] = useState(null);
  const seenCutIns = useRef(new Set());
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(!manual);
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    api.get("/weather/cities").then((r) => setCities(r.data)).catch(() => {});
    api.get("/weather/alerts").then((r) => {
      setAlerts(r.data.alerts || []);
      setStormMode(!!r.data.storm_mode);
    }).catch(() => {});
    api.get("/radar/velocity-status").then((r) => setVelocity(r.data || null)).catch(() => setVelocity(null));
    api.get("/cameras").then((r) => setCameras(r.data || [])).catch(() => setCameras([]));
    api.get("/broadcast/config").then((r) => setConfig(r.data.slides || [])).catch(() => setConfig([]));
    api.get("/bumper").then((r) => setBumper(r.data || null)).catch(() => setBumper(null));
    api.get("/sponsors").then((r) => setSponsors(r.data || [])).catch(() => setSponsors([]));
    api.get("/articles/latest").then((r) => setArticle(r.data || null)).catch(() => setArticle(null));
    api.get("/gameday?team=braves").then((r) => {
      const games = r.data?.games || [];
      // Pick next Braves home game (weather panel is meaningful only for home).
      const next = games.find((g) => g.is_home) || null;
      setBravesGame(next);
    }).catch(() => setBravesGame(null));
    // Football teams: next game (home or away — road-game panel is meaningful).
    api.get("/gameday?team=uga").then((r) => setUgaGame((r.data?.games || [])[0] || null)).catch(() => setUgaGame(null));
    api.get("/gameday?team=falcons").then((r) => setFalconsGame((r.data?.games || [])[0] || null)).catch(() => setFalconsGame(null));
    api.get("/gameday?team=gt").then((r) => setTechGame((r.data?.games || [])[0] || null)).catch(() => setTechGame(null));
    api.get("/ticker").then((r) => setTickerItems(r.data || [])).catch(() => setTickerItems([]));
    api.get("/tropical").then((r) => setTropical(r.data || null)).catch(() => setTropical(null));
    api.get("/aviation").then((r) => setAviation(r.data || null)).catch(() => setAviation(null));
    api.get("/forecast-videos").then((r) => { setForecastVideos(r.data || null); warmForecastVideos(r.data); }).catch(() => setForecastVideos(null));
    api.get("/promos").then((r) => setPromos(r.data || null)).catch(() => setPromos(null));
    api.get("/broadcast/fx").then((r) => setFx(r.data || null)).catch(() => {});
    const loadSpc = () => api.get("/spc/outlooks").then((r) => { setSpcDays((r.data?.days || []).filter((d) => d.ga_risk)); setSpcAll(r.data?.days || []); }).catch(() => {});
    loadSpc();
    const spcTimer = setInterval(loadSpc, 15 * 60 * 1000);
    const dataTimer = setInterval(() => {
      api.get("/weather/cities").then((r) => setCities(r.data)).catch(() => {});
      api.get("/weather/alerts").then((r) => {
        setAlerts(r.data.alerts || []);
        setStormMode(!!r.data.storm_mode);
      }).catch(() => {});
      api.get("/radar/velocity-status").then((r) => setVelocity(r.data || null)).catch(() => {});
      api.get("/cameras").then((r) => setCameras(r.data || [])).catch(() => {});
      api.get("/broadcast/config").then((r) => setConfig(r.data.slides || [])).catch(() => {});
      api.get("/sponsors").then((r) => setSponsors(r.data || [])).catch(() => {});
      api.get("/articles/latest").then((r) => setArticle(r.data || null)).catch(() => {});
      api.get("/gameday?team=braves").then((r) => {
        const games = r.data?.games || [];
        setBravesGame(games.find((g) => g.is_home) || null);
      }).catch(() => {});
      api.get("/gameday?team=uga").then((r) => setUgaGame((r.data?.games || [])[0] || null)).catch(() => {});
      api.get("/gameday?team=falcons").then((r) => setFalconsGame((r.data?.games || [])[0] || null)).catch(() => {});
      api.get("/gameday?team=gt").then((r) => setTechGame((r.data?.games || [])[0] || null)).catch(() => {});
      api.get("/ticker").then((r) => setTickerItems(r.data || [])).catch(() => {});
      api.get("/tropical").then((r) => setTropical(r.data || null)).catch(() => {});
      api.get("/aviation").then((r) => setAviation(r.data || null)).catch(() => {});
      api.get("/forecast-videos").then((r) => { setForecastVideos(r.data || null); warmForecastVideos(r.data); }).catch(() => {});
      api.get("/promos").then((r) => setPromos(r.data || null)).catch(() => {});
      api.get("/broadcast/fx").then((r) => setFx(r.data || null)).catch(() => {});
    }, 600_000);
    const clockTimer = setInterval(() => setNow(new Date()), 1000);
    return () => {
      clearInterval(dataTimer);
      clearInterval(clockTimer);
      clearInterval(spcTimer);
    };
  }, []);

  // Broadcast Cut-In: poll every 8s; a new cut-in overrides the loop until its dwell expires.
  useEffect(() => {
    let endTimer = null;
    const check = () => {
      api.get("/broadcast/cut-in").then((r) => {
        const a = r.data?.active;
        if (!a) { setCutIn(null); return; }
        if (seenCutIns.current.has(a.id)) {
          setCutIn((c) => (c && c.id === a.id ? { ...c, platforms: a.platforms, voice_url: a.voice_url || c.voice_url } : c));
          if (!endTimer) endTimer = setTimeout(() => setCutIn(null), Math.max(1000, (a.remaining_s || 0) * 1000));
          return;
        }
        seenCutIns.current.add(a.id);
        const dwellMs = (r.data.dwell_s || 60) * 1000;
        const remainingMs = Math.max(1000, (a.remaining_s || 0) * 1000);
        setCutIn({ ...a, dwellMs, endsAt: Date.now() + remainingMs });
        if (endTimer) clearTimeout(endTimer);
        endTimer = setTimeout(() => setCutIn(null), remainingMs);
      }).catch(() => {});
    };
    check();
    const t = setInterval(check, 8000);
    return () => { clearInterval(t); if (endTimer) clearTimeout(endTimer); };
  }, []);

  // Breaking-Weather Takeover: when a NEW warning (Tornado / Severe T-Storm /
  // Flash Flood) fires, slam a full-screen takeover in ahead of the alert board.
  // Seen warnings persist in localStorage so a page reload never re-triggers it.
  useEffect(() => {
    if (!fx?.takeover_enabled) return;
    if (seenWarnRef.current === null) {
      try { seenWarnRef.current = new Set(JSON.parse(localStorage.getItem("gawx_seen_warnings") || "[]")); }
      catch { seenWarnRef.current = new Set(); }
    }
    const seen = seenWarnRef.current;
    const warnings = (alerts || []).filter(isTakeoverWarning);
    const fresh = warnings.find((w) => w.id && !seen.has(w.id));
    if (!fresh) return;
    warnings.forEach((w) => w.id && seen.add(w.id));
    try { localStorage.setItem("gawx_seen_warnings", JSON.stringify([...seen].slice(-200))); } catch { /* ignore */ }
    const emergency = !!(fresh.tornado_emergency || fresh.pds);
    setTakeover({ id: fresh.id, alert: fresh, emergency, endsAt: Date.now() + (emergency ? 25000 : 8000) });
  }, [alerts, fx]);
  useEffect(() => {
    if (!takeover) return undefined;
    const ms = Math.max(0, takeover.endsAt - Date.now());
    const t = setTimeout(() => setTakeover(null), ms);
    return () => clearTimeout(t);
  }, [takeover]);

  const built = useMemo(() => {
    if (config === null) return { loop: [], all: [] };
    const liveFiltered = cameras.filter((c) => !_isCamDead(c.id));
    const liveCams = liveFiltered.length ? liveFiltered : cameras; // keep camera slides alive even if all were transiently benched
    const alertGroups = groupAlerts(alerts);

    // Build every possible slide (only those with data become renderable)
    const featured = cities.find((c) => c.id === "atlanta") || cities[0];
    const tonight = featured ? tonightPeriod(featured) : null;
    const tomorrow = featured ? tomorrowPeriod(featured) : null;

    const promoList = (promos?.ready ? promos.promos : []) || [];
    const promoByKey = Object.fromEntries(promoList.filter((p) => (p.clips || []).length).map((p) => [p.key, p]));

    const catalog = {
      intro: { fullBleed: true, chromeless: true, coldOpen: true, title: "GAWX Live Broadcast", node: <IntroSlide now={now} /> },
      alert: alerts.length
        ? { title: alertGroups.length > 1 ? `Severe Weather · ${alertGroups.length} Active Alerts` : `Severe Weather · ${alertGroups[0]?.title || ""}`, node: <AlertSlide alerts={alerts} /> }
        : null,
      velocity: velocity?.active
        ? { fullBleed: true, title: "Base Velocity", node: <VelocitySlide velocity={velocity} /> }
        : null,
      map: cities.length ? { fullBleed: true, asOf: true, title: "Current Temperatures", node: <GATempsSlide cities={cities} /> } : null,
      feature: featured ? { asOf: true, title: `Right Now · ${featured.name}`, node: <FeatureCitySlide city={featured} /> } : null,
      extremes: cities.length ? { asOf: true, title: "State Extremes", node: <ExtremesSlide cities={cities} /> } : null,
      bento: cities.length ? { asOf: true, title: "Six-City Snapshot", node: <BentoSlide cities={cities} /> } : null,
      "lows-tonight": tonight
        ? { fullBleed: true, title: `Low Temps ${tonight.name || "Tonight"}`, right: avgRight("Avg Low", cities.map((c) => tonightPeriod(c)?.temperature)), node: <TempOutlookSlide cities={cities} mode="lows" /> }
        : null,
      "highs-tomorrow": tomorrow
        ? { fullBleed: true, title: `High Temps ${tomorrow.name || "Tomorrow"}`, right: avgRight("Avg High", cities.map((c) => tomorrowPeriod(c)?.temperature)), node: <TempOutlookSlide cities={cities} mode="highs" /> }
        : null,
      seven: featured
        ? { title: `${featured.name} · 7‑Period`, node: <SevenDaySlide city={featured} /> }
        : null,
      futurecast: { fullBleed: true, title: "Futurecast Radar", right: <RadarLegend />, node: <TvRadarMap mode="futurecast" /> },
      "hrrr-reflectivity": { fullBleed: true, title: "Live Radar", right: <RadarLegend />, node: <TvRadarMap mode="live" durationMs={(config || []).find((c) => c.id === "hrrr-reflectivity")?.duration_ms || SLIDE_MS} /> },
      wind: cities.length
        ? { fullBleed: true, asOf: true, title: "Wind Gusts", right: <WindLegend />, node: <WindGustsSlide cities={cities} /> }
        : null,
      cameras: liveCams.length
        ? { title: "Live Cameras · Georgia", node: <LiveCameraWallSlide cameras={liveCams} /> }
        : null,
      "cameras-featured": liveCams.length
        ? { fullBleed: true, chromeless: true, title: "Live Camera", node: <FullScreenCamSlide cameras={liveCams} /> }
        : null,
      "forecast-video": (forecastVideos?.enabled && (forecastVideos?.videos || []).length)
        ? { fullBleed: true, chromeless: true, title: "Forecast", node: <ForecastVideoSlide videos={forecastVideos.videos} /> }
        : null,
      "ga-temps-map":  cities.length ? { fullBleed: true, asOf: true, title: "Temperatures",              node: <GATempsSlide cities={cities} /> } : null,
      "ga-feels-like": cities.length ? { fullBleed: true, asOf: true, title: "Feels Like",                node: <GAFeelsLikeSlide cities={cities} /> } : null,
      "ga-dewpoints":  cities.length ? { fullBleed: true, asOf: true, title: "Dewpoints",                 node: <GADewpointsSlide cities={cities} /> } : null,
      "ga-alerts-map": { fullBleed: true, title: "Georgia Active Alerts", right: <AlertsLegend groups={alertGroups} />, node: <GAAlertsMapSlide alerts={alerts} /> },
      "ga-outlook":    spcDays.length ? { fullBleed: true, title: "Severe Weather Outlook", right: <SpcLegend labels={Array.from(new Set(spcDays.flatMap((d) => d.features.map((f) => f.label))))} />, node: <GASevereOutlookSlide days={spcDays} durationMs={(config || []).find((c) => c.id === "ga-outlook")?.duration_ms || 12000} /> } : null,
      "ga-drought":    drought?.categories?.length ? { title: "GA Drought Monitor (USDM)", node: <DroughtSlide drought={drought} /> } : null,
      "outages":       outages?.active ? { title: "Power Outages", node: <OutagesSlide outages={outages} /> } : null,
      "storm-reports": stormReports?.active ? { fullBleed: true, title: "Storm Reports", right: <StormReportsLegend byCat={stormReports.by_cat} />, node: <StormReportsSlide reports={stormReports} /> } : null,
      "threat-index": (threat && threat.score >= 30) ? { title: "Storm Threat Index", node: <ThreatIndexSlide threat={threat} /> } : null,
      "safety": (threat && (threat.score >= 30 || threat.active_warnings > 0 || threat.active_watch)) ? { title: "Weather Safety", node: <SafetySlide threat={threat} /> } : null,
      "storm-arrival": (radarArrivals?.motion?.speed_mph >= 10 && (radarArrivals?.arrivals?.length || 0) >= 3) ? { title: "Storm Arrival Times", node: <StormArrivalSlide data={radarArrivals} /> } : null,
      "threat-summary": (threatSummary?.available && threatSummary?.headline) ? { title: "The Forecast In Plain Words", node: <ThreatSummarySlide summary={threatSummary} /> } : null,
      "severe-outlook-ahead": (spcAll || []).some((d) => SEVERE_RISKS.includes(d.ga_risk)) ? { title: "Severe Weather Outlook", node: <SevereOutlookSlide days={spcAll} /> } : null,
      "minutecast": hasMinutecast(radarArrivals?.minutecast) ? { title: "Minute-Cast", node: <MinutecastSlide minutecast={radarArrivals.minutecast} /> } : null,
      "outlook-score": hasOutlookScore(outlook) ? { title: "Daily Outlook Score", node: <OutlookScoreSlide data={outlook} /> } : null,
      "events":        events?.events?.length ? { title: "Georgia Event Weather", node: <EventsSlide events={events} /> } : null,
      "aviation":      (aviation?.airports || []).length ? { fullBleed: true, asOf: true, title: "Flight Conditions", right: <AviationLegend />, node: <TvAviationMap airports={aviation.airports} /> } : null,
      "aviation-board": (aviation?.airports || []).length ? { asOf: true, title: "Airport Conditions", node: <AviationBoardSlide aviation={aviation} /> } : null,
      "aviation-hazards": aviation?.has_hazards ? { title: "Aviation Hazards", node: <AviationHazardsSlide aviation={aviation} /> } : null,
      "tropical":      tropical?.active ? { title: tropical.systems.length > 1 ? `Tropical Update · ${tropical.systems.length} Systems` : `Tropical Update · ${tropical.systems[0].name}`, node: <TropicalUpdateSlide systems={tropical.systems} /> } : null,
      "tropical-cone": tropical?.active ? { fullBleed: true, title: "Storm Tracker", right: "National Hurricane Center", node: <TropicalConeSlide systems={tropical.systems} /> } : null,
      "tropical-outlook": tropical?.has_disturbances ? { title: "Tropical Formation Outlook", node: <TropicalOutlookSlide disturbances={tropical.disturbances} /> } : null,
      "impact":        impact ? { title: "Georgia Weather Impact",         node: <ImpactSlide impact={impact} /> } : null,
      "article":       article ? { title: "Latest Forecast Discussion",     node: <ArticleSlide article={article} /> } : null,
      "history":       historyFacts?.on_air ? { fullBleed: true, title: "On This Day in History", node: <HistorySlide facts={historyFacts} perMs={Math.max(5000, Math.floor(((config || []).find((c) => c.id === "history")?.duration_ms || 24000) / Math.max(1, historyFacts.items.length)))} /> } : null,
      "lakes":         lakes?.items?.some((l) => l.level != null) ? { title: "Georgia Lake Levels", node: <LakesSlide lakes={lakes} /> } : null,
      "coast":         coast?.beaches?.some((b) => b.tides?.length) ? { title: "Georgia Coast Board", node: <CoastSlide coast={coast} /> } : null,
      "hooch":         hooch?.flow_cfs != null ? { title: "Shoot the Hooch · Tubing", node: <HoochSlide hooch={hooch} cities={cities} /> } : null,
      "friday-night":  buildKickoffGames(cities).length ? { fullBleed: true, title: "Friday Night Lights", node: <FridayNightSlide cities={cities} /> } : null,
      "ag":            ag?.crops?.length ? { title: "Georgia Ag Weather", node: <AgSlide ag={ag} /> } : null,
      "pollen":        (pollen?.categories?.length && pollen?.count != null) ? { title: "Georgia Pollen Count", node: <PollenSlide pollen={pollen} /> } : null,
      "camera-map":    liveCams.length ? { title: "Georgia Camera Map",        node: <CameraMapSlide cameras={liveCams} /> } : null,
      "sponsor":       sponsors.length ? { title: "Sponsored By",             node: <SponsorSlide sponsors={sponsors} /> } : null,
      "promo-severe":  promoByKey.severe ? { fullBleed: true, chromeless: true, title: "Severe Weather Promo", clipCount: (promoByKey.severe.clips || []).length, node: <PromoSlide promo={promoByKey.severe} /> } : null,
      "promo-source":  promoByKey.source ? { fullBleed: true, chromeless: true, title: "Georgia's 24/7 Promo", clipCount: (promoByKey.source.clips || []).length, node: <PromoSlide promo={promoByKey.source} /> } : null,      "braves-gameday": bravesGame ? { title: "Braves Gameday Weather",       node: <GamedaySlide team="braves" game={bravesGame} /> } : null,
      "uga-gameday":     ugaGame ? { title: "Bulldogs Gameday Weather",        node: <GamedaySlide team="uga" game={ugaGame} /> } : null,
      "falcons-gameday": falconsGame ? { title: "Falcons Gameday Weather",     node: <GamedaySlide team="falcons" game={falconsGame} /> } : null,
      "tech-gameday":    techGame ? { title: "Yellow Jackets Gameday Weather", node: <GamedaySlide team="gt" game={techGame} /> } : null,
    };

    // If no config yet, use a permissive default order (intro + everything available)
    const cfgOrder = config.length
      ? config
      : Object.keys(catalog).map((id) => ({ id, enabled: true, duration_ms: SLIDE_MS }));

    // Every renderable slide (loop order first, then the rest) — used by the Forecast Studio picker
    const seen = new Set(cfgOrder.map((c) => c.id));
    const all = [...cfgOrder.map((c) => c.id), ...Object.keys(catalog).filter((id) => !seen.has(id))]
      .filter((id) => catalog[id] && id !== "forecast-video")
      .map((id) => ({ id, title: catalog[id].title, node: catalog[id].node, right: catalog[id].right, fullBleed: !!catalog[id].fullBleed, chromeless: !!catalog[id].chromeless }));

    const loop = cfgOrder
      .filter((c) => c.enabled && catalog[c.id] && !c.id.startsWith("promo-"))
      .map((c) => {
        const isAlert = c.id === "alert";
        const isTrop = c.id === "tropical" || c.id === "tropical-cone";
        // Alert + tropical slides cycle through every system; scale the slide's
        // loop time so the internal cycle finishes before the broadcast advances.
        const perAlert = isAlert && stormMode ? 12000 : (c.duration_ms || SLIDE_MS);
        const alertCount = Math.max(1, alertGroups.length);
        const perTrop = c.duration_ms || SLIDE_MS;
        const tropCount = Math.max(1, (tropical?.systems || []).length);
        const fvVids = forecastVideos?.videos || [];
        const fvTotal = fvVids.reduce((s, v) => s + (v.duration_s && v.duration_s > 1 ? v.duration_s : 60), 0);
        return {
          id: c.id,
          title: catalog[c.id].title,
          node: isAlert ? <AlertSlide alerts={alerts} perMs={perAlert} />
            : c.id === "tropical" ? <TropicalUpdateSlide systems={tropical?.systems || []} perMs={perTrop} />
            : c.id === "tropical-cone" ? <TropicalConeSlide systems={tropical?.systems || []} perMs={perTrop} />
            : catalog[c.id].node,
          right: catalog[c.id].right,
          fullBleed: !!catalog[c.id].fullBleed,
          chromeless: !!catalog[c.id].chromeless,
          coldOpen: !!catalog[c.id].coldOpen,
          asOf: !!catalog[c.id].asOf,
          durationMs: isAlert ? perAlert * alertCount
            : isTrop ? perTrop * tropCount
            : c.id === "forecast-video" ? Math.max(8000, Math.round(fvTotal * 1000) + fvVids.length * 700)
            : c.id.startsWith("promo-") ? ((catalog[c.id].clipCount || 3) * 10000 + 2000)
            : (c.duration_ms || SLIDE_MS),
          _isAlert: isAlert,
        };
      })
      .sort((a, b) => {
        // Storm mode: push the alert slide to position 0 (first thing viewers see)
        if (stormMode) {
          if (a._isAlert && !b._isAlert) return -1;
          if (b._isAlert && !a._isAlert) return 1;
        }
        return 0;
      });

    // Velocity board: during a Tornado / Severe T-storm Warning, slot the storm-relative velocity slide
    // right after the alert board so rotation coverage always airs, even if not in the admin loop order.
    if (velocity?.active && catalog.velocity && !loop.some((s) => s.id === "velocity")) {
      const vEntry = {
        id: "velocity", title: catalog.velocity.title, node: catalog.velocity.node,
        right: undefined, fullBleed: true, chromeless: false, coldOpen: false, asOf: false, durationMs: 12000,
      };
      const alertIdx = loop.findIndex((s) => s._isAlert || s.id === "alert");
      if (alertIdx >= 0) loop.splice(alertIdx + 1, 0, vEntry);
      else loop.unshift(vEntry);
    }

    // Power-outage board: airs only when statewide customers-out crosses the threshold. Slot it after the
    // alert/velocity coverage (or at the front in storm mode) even if it isn't in the admin loop order.
    if (outages?.active && catalog.outages && !loop.some((s) => s.id === "outages")) {
      const oEntry = {
        id: "outages", title: catalog.outages.title, node: catalog.outages.node,
        right: undefined, fullBleed: false, chromeless: false, coldOpen: false, asOf: false,
        durationMs: (cfgOrder.find((c) => c.id === "outages")?.duration_ms) || SLIDE_MS,
      };
      const vIdx = loop.findIndex((s) => s.id === "velocity");
      const aIdx = loop.findIndex((s) => s._isAlert || s.id === "alert");
      const at = vIdx >= 0 ? vIdx + 1 : aIdx >= 0 ? aIdx + 1 : stormMode ? 0 : loop.length;
      loop.splice(at, 0, oEntry);
    }

    // Storm Reports board (NWS LSRs): airs only when recent GA spotter reports exist. Slot after alert/velocity.
    if (stormReports?.active && catalog["storm-reports"] && !loop.some((s) => s.id === "storm-reports")) {
      const sEntry = {
        id: "storm-reports", title: catalog["storm-reports"].title, node: catalog["storm-reports"].node,
        right: catalog["storm-reports"].right, fullBleed: true, chromeless: false, coldOpen: false, asOf: false,
        durationMs: (cfgOrder.find((c) => c.id === "storm-reports")?.duration_ms) || SLIDE_MS,
      };
      const vIdx2 = loop.findIndex((s) => s.id === "velocity");
      const aIdx2 = loop.findIndex((s) => s._isAlert || s.id === "alert");
      const at2 = vIdx2 >= 0 ? vIdx2 + 1 : aIdx2 >= 0 ? aIdx2 + 1 : stormMode ? 0 : loop.length;
      loop.splice(at2, 0, sEntry);
    }

    // Storm Threat Index board (score >= 30): a persistent-feel threat meter. Lead the loop in storm mode,
    // otherwise sit near the front so viewers see the day's severe risk early.
    if (threat && threat.score >= 30 && catalog["threat-index"] && !loop.some((s) => s.id === "threat-index")) {
      const tEntry = {
        id: "threat-index", title: catalog["threat-index"].title, node: catalog["threat-index"].node,
        right: undefined, fullBleed: false, chromeless: false, coldOpen: false, asOf: false,
        durationMs: (cfgOrder.find((c) => c.id === "threat-index")?.duration_ms) || SLIDE_MS,
      };
      const aIdx3 = loop.findIndex((s) => s._isAlert || s.id === "alert");
      const at3 = stormMode && aIdx3 >= 0 ? aIdx3 + 1 : stormMode ? 0 : Math.min(1, loop.length);
      loop.splice(at3, 0, tEntry);
    }

    // Weather Safety board: airs when the threat is elevated or a watch/warning is active. Place it late in the
    // loop (recurring reminder) so it doesn't crowd the lead coverage.
    if (threat && (threat.score >= 30 || threat.active_warnings > 0 || threat.active_watch) && catalog.safety && !loop.some((s) => s.id === "safety")) {
      const safeEntry = {
        id: "safety", title: catalog.safety.title, node: catalog.safety.node,
        right: undefined, fullBleed: false, chromeless: false, coldOpen: false, asOf: false,
        durationMs: (cfgOrder.find((c) => c.id === "safety")?.duration_ms) || SLIDE_MS,
      };
      loop.push(safeEntry);
    }

    // Storm Arrival Times board (radar-derived): airs when a line of storms is moving across GA. Slot near the
    // front (after alert/velocity) so viewers see "when it reaches my town" early.
    if (radarArrivals?.motion?.speed_mph >= 10 && (radarArrivals?.arrivals?.length || 0) >= 3 && catalog["storm-arrival"] && !loop.some((s) => s.id === "storm-arrival")) {
      const arrEntry = {
        id: "storm-arrival", title: catalog["storm-arrival"].title, node: catalog["storm-arrival"].node,
        right: undefined, fullBleed: false, chromeless: false, coldOpen: false, asOf: false,
        durationMs: (cfgOrder.find((c) => c.id === "storm-arrival")?.duration_ms) || SLIDE_MS,
      };
      const vIdx3 = loop.findIndex((s) => s.id === "velocity");
      const aIdx4 = loop.findIndex((s) => s._isAlert || s.id === "alert");
      const at4 = vIdx3 >= 0 ? vIdx3 + 1 : aIdx4 >= 0 ? aIdx4 + 1 : stormMode ? 0 : Math.min(1, loop.length);
      loop.splice(at4, 0, arrEntry);
    }

    // AI "Forecast in plain words" board: a general daily summary of the NWS discussion. Airs once per rotation
    // in a calm mid-loop slot (not storm-gated — it's an everyday weather-story board).
    if (threatSummary?.available && threatSummary?.headline && catalog["threat-summary"] && !loop.some((s) => s.id === "threat-summary")) {
      const sumEntry = {
        id: "threat-summary", title: catalog["threat-summary"].title, node: catalog["threat-summary"].node,
        right: undefined, fullBleed: false, chromeless: false, coldOpen: false, asOf: false,
        durationMs: (cfgOrder.find((c) => c.id === "threat-summary")?.duration_ms) || SLIDE_MS,
      };
      loop.splice(Math.min(2, loop.length) + Math.floor((loop.length - 2) / 2), 0, sumEntry);
    }

    // Severe Weather Outlook board (SPC Day 1-3): airs when severe is in the multi-day outlook. Near the front.
    if ((spcAll || []).some((d) => SEVERE_RISKS.includes(d.ga_risk)) && catalog["severe-outlook-ahead"] && !loop.some((s) => s.id === "severe-outlook-ahead")) {
      const soEntry = {
        id: "severe-outlook-ahead", title: catalog["severe-outlook-ahead"].title, node: catalog["severe-outlook-ahead"].node,
        right: undefined, fullBleed: false, chromeless: false, coldOpen: false, asOf: false,
        durationMs: (cfgOrder.find((c) => c.id === "severe-outlook-ahead")?.duration_ms) || SLIDE_MS,
      };
      const aIdx5 = loop.findIndex((s) => s._isAlert || s.id === "alert" || s.id === "threat-index");
      loop.splice(aIdx5 >= 0 ? aIdx5 + 1 : Math.min(1, loop.length), 0, soEntry);
    }

    // Minute-Cast board: airs when rain is ongoing/approaching across multiple cities. Slot after storm-arrival.
    if (hasMinutecast(radarArrivals?.minutecast) && catalog.minutecast && !loop.some((s) => s.id === "minutecast")) {
      const mcEntry = {
        id: "minutecast", title: catalog.minutecast.title, node: catalog.minutecast.node,
        right: undefined, fullBleed: false, chromeless: false, coldOpen: false, asOf: false,
        durationMs: (cfgOrder.find((c) => c.id === "minutecast")?.duration_ms) || SLIDE_MS,
      };
      const arrI = loop.findIndex((s) => s.id === "storm-arrival");
      loop.splice(arrI >= 0 ? arrI + 1 : Math.min(2, loop.length), 0, mcEntry);
    }

    // Daily Outlook Score board (planner): an everyday 7-day 1-10 disruption score. Airs once per rotation
    // in a calm mid-loop slot (not storm-gated — it's a "plan your week" board that keeps viewers checking back).
    if (hasOutlookScore(outlook) && catalog["outlook-score"] && !loop.some((s) => s.id === "outlook-score")) {
      const osEntry = {
        id: "outlook-score", title: catalog["outlook-score"].title, node: catalog["outlook-score"].node,
        right: undefined, fullBleed: false, chromeless: false, coldOpen: false, asOf: false,
        durationMs: (cfgOrder.find((c) => c.id === "outlook-score")?.duration_ms) || SLIDE_MS,
      };
      loop.splice(Math.min(3, loop.length) + Math.floor((Math.max(0, loop.length - 3)) / 2), 0, osEntry);
    }

    // Cold open: guarantee the branded show open plays at every loop restart
    // (slide 0) regardless of where it sits in the admin order. Skipped in storm
    // mode so viewers hit the severe alert immediately.
    let finalLoop = loop;
    if (!stormMode) {
      const rest = loop.filter((s) => s.id !== "intro");
      finalLoop = [{
        id: "intro",
        title: catalog.intro.title,
        node: catalog.intro.node,
        right: undefined,
        fullBleed: true,
        chromeless: true,
        coldOpen: true,
        asOf: false,
        durationMs: (cfgOrder.find((c) => c.id === "intro")?.duration_ms) || 6000,
      }, ...rest];
    }
    return { loop: finalLoop, all };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cities, alerts, cameras, config, stormMode, velocity, outages, stormReports, threat, radarArrivals, threatSummary, outlook, spcAll, sponsors, bravesGame, ugaGame, falconsGame, techGame, impact, historyFacts, lakes, coast, pollen, drought, hooch, ag, events, article, spcDays, tropical, aviation, forecastVideos, promos]);
  const slides = built.loop;

  // Prepend the On-Air Bumper as slide 0 when enabled. In storm mode we skip
  // the bumper so viewers see the severe alert immediately.
  const slidesWithBumper = useMemo(() => {
    if (stormMode) return slides;
    if (!bumper?.enabled || !bumper?.video_url) return slides;
    const bumperSlide = {
      id: "bumper",
      title: "On-Air · GAWX",
      node: <BumperSlide videoUrl={bumper.video_url} />,
      durationMs: bumper.duration_ms || 6000,
    };
    return [bumperSlide, ...slides];
  }, [slides, bumper, stormMode]);

  const safeIdx = Math.min(idx, Math.max(slidesWithBumper.length - 1, 0));
  const _active = slidesWithBumper[safeIdx];
  // Promos (fixed-length 30s spots) and recorded forecast videos must play in FULL —
  // they ignore the global per-slide duration override used for the capture URL
  // (otherwise the stream cuts them off after ~8s). Their own durationMs tracks the
  // real clip length (promo clip count / sum of each video's duration).
  const _activeId = _active?.id || "";
  const _playInFull = _activeId.startsWith("promo-") || _activeId === "forecast-video";
  const currentDurationMs = _playInFull
    ? (_active?.durationMs ?? SLIDE_MS)
    : (durationOverride ?? _active?.durationMs ?? SLIDE_MS);

  // "On the 8s" bumper: at clock minutes ending in 8, roll the local-conditions
  // segment and overlay a branded bumper over it (TWC-style). One fire per slot.
  useEffect(() => {
    if (!fx?.on_the_eights_enabled || slidesWithBumper.length <= 1) return;
    if (cutIn || takeover) return;
    const m = now.getMinutes();
    if (m % 10 !== 8 || now.getSeconds() >= 3) return;
    const slot = `${now.getHours()}:${m}`;
    if (eightsSlotRef.current === slot) return;
    eightsSlotRef.current = slot;
    const target = slidesWithBumper.findIndex((s) => CONDITIONS_SLIDE_ORDER.includes(s.id));
    if (target >= 0) setIdx(target);
    setEights({ slot, endsAt: Date.now() + 7000 });
  }, [now, fx, slidesWithBumper, cutIn, takeover]);
  useEffect(() => {
    if (!eights) return undefined;
    const t = setTimeout(() => setEights(null), Math.max(0, eights.endsAt - Date.now()));
    return () => clearTimeout(t);
  }, [eights]);

  // Hard client-side expiry so an override can never outlive its window.
  const liveTakeover = takeover && now.getTime() < (takeover.endsAt || 0) ? takeover : null;
  const liveEights = !liveTakeover && eights && now.getTime() < (eights.endsAt || 0) ? eights : null;

  useEffect(() => {
    if (!playing || cutIn || liveTakeover || liveEights || slidesWithBumper.length <= 1) return undefined;
    const t = setTimeout(() => setIdx((i) => (i + 1) % slidesWithBumper.length), currentDurationMs);
    return () => clearTimeout(t);
  }, [idx, playing, cutIn, liveTakeover, liveEights, slidesWithBumper.length, currentDurationMs]);

  useEffect(() => {
    if (idx >= slidesWithBumper.length) setIdx(0);
  }, [slidesWithBumper.length, idx]);

  // Hard client-side expiry: a cut-in can never outlive its dwell window, even if the API poll fails or a timer was lost.
  const liveCutIn = cutIn && now.getTime() < (cutIn.endsAt || 0) ? cutIn : null;
  const cutInNode = liveCutIn ? <CutInSlide cutIn={liveCutIn} now={now} /> : null;
  const liveSpotlight = spotlight && now.getTime() < (spotlight.endsAt || 0) ? spotlight : null;
  const spotlightNode = liveSpotlight ? <TownSpotlightSlide spotlight={liveSpotlight} /> : null;

  const tickerFeed = useMemo(
    () => buildTickerFeed({ alerts, cities, spcDays, tropical: tropical?.systems || [], games: [bravesGame, ugaGame, falconsGame, techGame], adminItems: tickerItems, stormReports, now }),
    [alerts, cities, spcDays, tropical, bravesGame, ugaGame, falconsGame, techGame, tickerItems, stormReports, Math.floor(now.getTime() / 60000)]  // eslint-disable-line react-hooks/exhaustive-deps
  );
  const takeoverNode = liveTakeover ? <AlertTakeover alert={liveTakeover.alert} emergency={liveTakeover.emergency} playTone={capture && !!fx?.takeover_tone} toneUrl={fx?.tone_url} /> : null;
  const eightsNode = liveEights ? <OnTheEightsSlide now={now} cities={cities} /> : null;

  return { slides: slidesWithBumper, allSlides: built.all, idx: safeIdx, setIdx, playing, setPlaying, now, durationMs: currentDurationMs, tickerItems, tickerFeed, cutIn: liveCutIn, cutInNode, spotlight: liveSpotlight, spotlightNode, takeover: liveTakeover, takeoverNode, eights: liveEights, eightsNode, cities, cameras, sponsors };
}

export { BroadcastTicker };

export default function BroadcastGraphics() {
  const { slides, idx, setIdx, playing, setPlaying, now, durationMs, tickerFeed, cutIn, cutInNode, spotlight, spotlightNode, takeover, takeoverNode, eights, eightsNode, cities } = useBroadcastState();
  const chatTip = useChatTip();
  const frameRef = useRef(null);
  const [scale, setScale] = useState(0.5);
  useEffect(() => {
    const el = frameRef.current;
    if (!el) return undefined;
    const fit = () => setScale(el.clientWidth / 1920);
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    return () => ro.disconnect();
  }, [slides.length]);

  if (!slides.length) return null;
  const current = slides[idx];
  const atlanta = (cities || []).find((c) => c.id === "atlanta") || (cities || [])[0];
  const tickerUrgent = tickerFeed.some((i) => i.urgent);
  const tickerLabel = tickerUrgent ? "Alert" : "GAWX Live";

  const timeStr = now.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
  const dayStr = now.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });

  return (
    <section id="broadcast" className="relative py-14">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="mb-6 flex items-end justify-between">
          <div>
            <div className="font-oswald text-xs uppercase tracking-[0.3em] text-[#d90429]">On‑Air Rotation</div>
            <h2 className="font-oswald text-2xl font-bold uppercase tracking-wider text-white sm:text-3xl lg:text-4xl">
              Broadcast Graphics Loop
            </h2>
          </div>
          <div className="hidden items-center gap-3 sm:flex">
            <span className="font-mono text-xs uppercase tracking-widest text-gray-400">
              {slides.length} graphics · {durationMs / 1000}s each
            </span>
            <a
              href="/broadcast?chrome=off"
              target="_blank"
              rel="noreferrer"
              data-testid="broadcast-fullscreen-link"
              className="flex items-center gap-2 rounded-sm border border-[#d90429] bg-[#d90429]/10 px-3 py-2 font-oswald text-xs font-bold uppercase tracking-widest text-white hover:bg-[#d90429]/30"
            >
              <Maximize2 className="h-3.5 w-3.5" /> Fullscreen · Export
            </a>
          </div>
        </div>

        {/* Faithful miniature of the 1920x1080 on-air stage, scaled to the container width */}
        <div
          ref={frameRef}
          data-testid="broadcast-frame"
          className="relative aspect-video overflow-hidden rounded border border-white/10 bg-black shadow-[0_20px_60px_rgba(0,0,0,0.6)]"
        >
          <div className="absolute left-0 top-0 origin-top-left" style={{ width: 1920, height: 1080, transform: `scale(${scale})` }} data-testid="broadcast-preview-stage">
            {takeover ? (
              <div key={`takeover-${takeover.id}`} data-testid="broadcast-takeover" className="absolute inset-0 broadcast-fade">{takeoverNode}</div>
            ) : cutIn ? (
              <div key={`cutin-${cutIn.id}`} className="absolute inset-0 broadcast-fade">{cutInNode}</div>
            ) : spotlight ? (
              <div key={`spotlight-${spotlight.id}`} data-testid="broadcast-spotlight" className="absolute inset-0 broadcast-fade">{spotlightNode}</div>
            ) : eights ? (
              <div key={`eights-${eights.slot}`} data-testid="broadcast-eights" className="absolute inset-0 broadcast-fade">{eightsNode}</div>
            ) : (
              <div key={current.id} data-testid={`broadcast-slide-${current.id}`} className="absolute inset-0 broadcast-fade">
                <TvFrame title={current.title} right={current.right} bugTemp={atlanta?.current_temp} fullBleed={current.fullBleed} chromeless={current.chromeless} coldOpen={current.coldOpen} asOf={current.asOf} noDog={(current.id || "").startsWith("promo-")} hasTicker>
                  {current.node}
                </TvFrame>
              </div>
            )}
            <SlideStinger key={`sting-${takeover ? `tk-${takeover.id}` : cutIn ? `cutin-${cutIn.id}` : eights ? `e8-${eights.slot}` : current.id}`} variant={takeover || cutIn ? "radar" : eights ? "wipe" : stingerForSlide(current)} testId="broadcast-stinger" />
            {!cutIn && !takeover && !eights && !spotlight && <NextUpBug title={slides[(idx + 1) % slides.length]?.title} />}
            {!cutIn && !takeover && <TvTicker label={tickerLabel} items={tickerFeed} urgent={tickerUrgent} />}
            {!cutIn && !takeover && !spotlight && <ChatTipBug tip={chatTip} />}
          </div>

          {/* Progress bar */}
          <div className="absolute inset-x-0 bottom-0 z-10 h-1 bg-white/10">
            <div
              key={`${idx}-${playing ? "p" : "s"}`}
              className="h-full bg-[#d90429]"
              style={{
                width: playing ? "100%" : "0%",
                transition: playing ? `width ${durationMs}ms linear` : "none",
              }}
            />
          </div>
        </div>
        <div className="mt-2 flex items-center justify-between font-mono text-[11px] uppercase tracking-widest text-gray-400">
          <span>{dayStr} · {timeStr}</span>
          <span>{cutIn ? "Breaking · Cut-In" : takeover ? "Breaking Weather · Takeover" : spotlight ? "Town Shoutout" : eights ? "On the 8s" : current.title} · {idx + 1} / {slides.length}</span>
        </div>

        {/* Controls */}
        <div className="mt-4 flex flex-col items-center justify-between gap-3 sm:flex-row">
          <div className="flex items-center gap-2">
            <button
              data-testid="broadcast-prev"
              onClick={() => setIdx((idx - 1 + slides.length) % slides.length)}
              className="flex h-9 w-9 items-center justify-center rounded-sm border border-white/15 text-white hover:border-[#d90429] hover:text-[#d90429]"
              aria-label="Previous slide"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              data-testid="broadcast-toggle"
              onClick={() => setPlaying(!playing)}
              className="flex items-center gap-2 rounded-sm border border-white/15 px-3 py-2 font-oswald text-xs font-bold uppercase tracking-widest text-white hover:border-[#d90429] hover:text-[#d90429]"
            >
              {playing ? <><Pause className="h-3.5 w-3.5" /> Pause</> : <><Play className="h-3.5 w-3.5" /> Play</>}
            </button>
            <button
              data-testid="broadcast-next"
              onClick={() => setIdx((idx + 1) % slides.length)}
              className="flex h-9 w-9 items-center justify-center rounded-sm border border-white/15 text-white hover:border-[#d90429] hover:text-[#d90429]"
              aria-label="Next slide"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
            <a
              href="/broadcast?chrome=off"
              target="_blank"
              rel="noreferrer"
              className="ml-2 flex items-center gap-2 rounded-sm border border-white/15 px-3 py-2 font-oswald text-xs font-bold uppercase tracking-widest text-white hover:border-[#d90429] hover:text-[#d90429] sm:hidden"
            >
              <Maximize2 className="h-3.5 w-3.5" /> Fullscreen
            </a>
          </div>

          <div data-testid="broadcast-dots" className="flex items-center gap-1.5">
            {slides.map((s, i) => (
              <button
                key={s.id}
                data-testid={`broadcast-dot-${s.id}`}
                onClick={() => setIdx(i)}
                aria-label={`Go to ${s.title}`}
                className={`h-1.5 rounded-full transition-all ${
                  i === idx ? "w-8 bg-[#d90429]" : "w-1.5 bg-white/20 hover:bg-white/40"
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
